export type SkillCategory =
  | 'Front-end'
  | 'Back-end'
  | 'Mobile'
  | 'Bases de données'
  | 'Outils / DevOps';

export interface Identity {
  pseudo: string;
  fullName: string;
  birthYear: number;
  title: string;
  location: string;
}

export interface About {
  headline: string;
  summary: string;
  highlights: string[];
}

export interface Experience {
  role: string;
  company: string;
  period: string;
  description: string;
  achievements: string[];
  stack: string[];
}

export interface Education {
  degree: string;
  school: string;
  year: string;
  description?: string;
}

export interface Project {
  name: string;
  tagline: string;
  description: string;
  role: string;
  period: string;
  status: string;
  highlights: string[];
  stack: string[];
  link: string;
}

export interface Skill {
  name: string;
  level?: string;
  icon: string;
  category: SkillCategory;
}

export interface Contact {
  email: string;
  linkedin: string;
  github?: string;
  cvUrl?: string;
}

export interface PortfolioData {
  identity: Identity;
  about: About;
  experiences: Experience[];
  projects: Project[];
  education: Education[];
  skills: Skill[];
  contact: Contact;
}

export const portfolioData: PortfolioData = {
  identity: {
    pseudo: 'Draed',
    fullName: 'Nicolas DERUELLE',
    birthYear: 1997,
    title: 'Lead Developer ERP',
    location: 'Le Haillan, France'
  },

  about: {
    headline: 'Concevoir des systèmes fiables et des solutions adaptées au besoin métier.',
    summary:
      "Lead Developer spécialisé ERP avec une forte capacité d'analyse et d'adaptation technique. J'aime comprendre les problématiques métiers pour proposer des solutions robustes et maintenables. Mon parcours mêle développement logiciel, intégration de systèmes et conception technique.",
    highlights: [
      "Analyse rapide des besoins et choix d'architecture adaptés",
      "Capacité à intervenir sur des environnements techniques variés",
      "Référent technique et accompagnement d'équipe"
    ]
  },

  experiences: [
    {
      role: 'Lead Developer ERP',
      company: 'Groupe Qérys',
      period: 'Février 2025 — Aujourd’hui',
      description:
        "Référent technique au sein de l'équipe de développement ERP. Supervision des choix techniques, accompagnement des développeurs et participation active au développement et à l'évolution du système d'information.",
      achievements: [
        "Support technique et accompagnement d'une équipe de 5 développeurs",
        "Participation aux choix techniques et à l'évolution de l'architecture applicative",
        "Amélioration et maintien des intégrations entre ERP et systèmes externes"
      ],
      stack: ['Java', 'JAVEX', 'RPG', 'EDI', 'ERP M3', 'Editique StreamServe']
    },
    {
      role: 'Développeur ERP M3',
      company: 'Groupe Qérys',
      period: 'Juillet 2019 — Février 2025',
      description:
        "Développement et évolution de l'ERP M3. Création de programmes, intégration de flux EDI et mise en place d'interfaces entre différents systèmes du SI.",
      achievements: [
        "Développement et maintenance de programmes en RPG et JAVEX",
        "Mise en place de mappings EDI pour automatiser les échanges partenaires",
        "Développement d'interfaces entre ERP et WMS"
      ],
      stack: ['Java', 'JAVEX', 'RPG', 'EDI', 'ERP M3', 'Editique StreamServe']
    },
    {
      role: 'Apprenti Développeur',
      company: 'SGAMI Sud-Ouest',
      period: 'Septembre 2017 — Juillet 2019',
      description:
        "Développement d'un outil interne permettant de migrer des bases de données de manière sélective entre serveurs.",
      achievements: [
        "Conception et développement complet d'un outil de migration",
        "Migration sélective par employé ou service",
        "Automatisation et sécurisation des opérations de transfert de données"
      ],
      stack: ['Java', 'SQL']
    },
    {
      role: 'Stagiaire Développement & Électronique',
      company: 'Skinlabs',
      period: 'Avril 2017 — Juillet 2017',
      description:
        "Conception d’une carte électronique permettant de piloter une sonde de cryothérapie depuis un Raspberry Pi, avec mise en place d’un correcteur PID pour la régulation.",
      achievements: [
        "Analyse du besoin et conception de la solution technique",
        "Conception d'une carte électronique prototype",
        "Développement du logiciel de communication",
        "Support SAV et préparation à la production"
      ],
      stack: ['Arduino', 'Raspberry Pi', 'Électronique']
    }
  ],

  projects: [
    {
  name: 'WishTogether',
  tagline: 'Plateforme collaborative de listes de souhaits, cagnottes et cadeaux.',
  description:
    "WishTogether est une plateforme web pensée pour simplifier l'organisation des cadeaux et événements en groupe. L'application permet de créer des listes de souhaits, partager des idées cadeaux, suivre les participations et centraliser les contributions dans une expérience fluide, moderne et orientée conversion.",
  role: 'Conception produit, développement full-stack et mise en production',
  period: '2025 — Aujourd’hui',
  status: 'En production',
  highlights: [
    'Création et partage de listes de souhaits en quelques étapes',
    'Gestion des participations, réservations et contributions utilisateurs',
    'Architecture pensée pour un produit monétisable avec expérience utilisateur soignée'
  ],
  stack: ['Angular', 'TypeScript', 'SCSS', 'Node.js', 'Express', 'Firebase', 'Stripe'],
  link: 'https://wishtogether.fr'
}
  ],

  education: [
    {
      degree: 'BTS Systèmes Informatiques des Organisations',
      school: 'CFA Gustave Eiffel — Bordeaux',
      year: '2019'
    },
    {
      degree: 'DUT Génie Électrique et Informatique Industrielle',
      school: 'IUT — Bordeaux',
      year: '2017'
    },
    {
      degree: 'Bac Scientifique',
      school: 'Lycée Polyvalent du Taaone — Papeete',
      year: '2015'
    }
  ],

  skills: [
    { name: 'Angular', level: 'Avancé', icon: 'angular', category: 'Front-end' },
    { name: 'TypeScript', level: 'Avancé', icon: 'code', category: 'Front-end' },
    { name: 'SCSS', level: 'Avancé', icon: 'palette', category: 'Front-end' },

    { name: 'Java', level: 'Avancé', icon: 'server', category: 'Back-end' },
    { name: 'REST API', level: 'Avancé', icon: 'network', category: 'Back-end' },

    { name: 'Java (Android)', level: 'Intermédiaire', icon: 'mobile', category: 'Mobile' },

    { name: 'SQL', level: 'Avancé', icon: 'database', category: 'Bases de données' },
    { name: 'PostgreSQL', level: 'Intermédiaire', icon: 'database', category: 'Bases de données' },

    { name: 'Git', level: 'Avancé', icon: 'git', category: 'Outils / DevOps' },
    { name: 'Linux', level: 'Intermédiaire', icon: 'terminal', category: 'Outils / DevOps' }
  ],

  contact: {
    email: 'nicoq1259@gmail.com',
    linkedin: 'https://fr.linkedin.com/in/nicolas-deruelle-445145144'
  }
};
